import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginHrmsUser } from "../../../Redux/slice/features/authSlice";
import { REACT_APP_API_URL } from "../../../env";

const SigninHrms = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    employeeID: "",
    password: "",
  });

  const [captchaText, setCaptchaText] = useState("");
  const [captchaId, setCaptchaId] = useState("");
  const [captchaValue, setCaptchaValue] = useState("");
  const [toast, setToast] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadCaptcha = async () => {
    try {
      const res = await fetch(`${REACT_APP_API_URL}api/hrms/auth/captcha`);
      const data = await res.json();

      setCaptchaText(data.captcha);
      setCaptchaId(data.captchaId);
      setCaptchaValue("");
    } catch {
      setToast("Unable to load captcha");
      setTimeout(() => setToast(""), 4000);
    }
  };

  useEffect(() => {
    loadCaptcha();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    if (!captchaId || captchaValue === "") {
      setToast("Please enter captcha");
      setTimeout(() => setToast(""), 4000);
      setIsSubmitting(false);
      return;
    }

    if (captchaValue !== captchaText) {
      setToast("Captcha must match exactly, including uppercase and lowercase letters");
      loadCaptcha();
      setTimeout(() => setToast(""), 5000);
      setIsSubmitting(false);
      return;
    }

    try {
      const res = await dispatch(
        loginHrmsUser({
          employee_id: formData.employeeID,
          password: formData.password,
          captchaId,
          captchaValue,
        })
      );

      if (res.meta.requestStatus === "rejected") {
        setToast(res.payload || "Check your credentials and try again");
        loadCaptcha();
        setTimeout(() => setToast(""), 5000);
        return;
      }
      sessionStorage.setItem("hrmsUser", JSON.stringify(res.payload));
      sessionStorage.setItem("hrmsLogin", "true");

      navigate("/hrms", { replace: true });
    } catch {
      setToast("Something went wrong. Try again!");
      loadCaptcha();
      setTimeout(() => setToast(""), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Auto redirect if logged in
  useEffect(() => {
    const isSessionActive = sessionStorage.getItem("hrmsLogin") === "true";
    if (isSessionActive && location.pathname !== "/hrms") {
      navigate("/hrms", { replace: true });
    }
  }, [location.pathname, navigate]);

  return (
    <div className="px-xl-5 px-4 auth-body">
      <form onSubmit={handleSubmit}>
        <ul className="row g-3 list-unstyled li_animate">
          <h5 className="mb-4 text-center display-9 fw-bold text-primary">
            HRMS Login
          </h5>

          <li className="col-12">
            <label className="form-label">Employee ID</label>
            <input
              type="text"
              name="employeeID"
              className="form-control form-control-lg"
              placeholder="Enter your employeeID"
              value={formData.employeeID}
              onChange={handleChange}
              required
            />
          </li>

          <li className="col-12">
            <div className="form-label">Password</div>
            <div className="input-group">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                className="form-control form-control-lg"
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleChange}
                required
              />
              <button
                type="button"
                className="btn btn-light border shadow-none"
                tabIndex={-1}
                onClick={() => setShowPassword((prev) => !prev)}
                style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
              >
                {showPassword ? (
                  <i className="bi bi-eye-slash"></i>
                ) : (
                  <i className="bi bi-eye"></i>
                )}
              </button>
            </div>
          </li>

          <li className="col-12">
            <label className="form-label">Captcha</label>

            <div className="input-group input-group-lg">
              <span
                className="input-group-text fw-bold bg-light"
                style={{
                  letterSpacing: "6px",
                  fontFamily: "monospace",
                  userSelect: "none",
                }}
              >
                {captchaText}
              </span>

              <button
                type="button"
                className="btn"
                onClick={loadCaptcha}
                title="Refresh Captcha"
              >
                ↻
              </button>

              <input
                type="text"
                className="form-control text-center"
                placeholder="Enter captcha"
                value={captchaValue}
                onChange={(e) => setCaptchaValue(e.target.value)}
                autoComplete="off"
                required
              />
            </div>
          </li>

          <li className="col-12 my-lg-4">
            <button
              type="submit"
              className="btn btn-lg w-100 btn-primary text-uppercase mb-2"
              disabled={loading || isSubmitting}
            >
              {loading ? "Signing in..." : "SIGN IN"}
            </button>

            <Link
              to="/login"
              onClick={() => {
                sessionStorage.removeItem("hrmsLogin");
                sessionStorage.removeItem("adminLogin");
                // resetCaptcha();
              }}
              className="d-block mt-2 text-decoration-underline text-primary"
            >
              Login as Admin/Manager
            </Link>
          </li>

          {toast && (
            <div
              className={`alert mt-2 text-center ${/captcha/i.test(toast) ? "alert-warning" : "alert-danger"
                }`}
              style={{ minWidth: "300px" }}
            >
              {toast}
            </div>
          )}
        </ul>
      </form>
    </div>
  );
};

export default SigninHrms;
